import React, { useState, useEffect } from 'react';
import {
  Container, Table, Button, Card, Badge, Alert, Spinner,
  Modal, Form, Row, Col, Dropdown, ButtonGroup, InputGroup,
  Pagination
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { exportService } from '../../services/exportService';
import { importService } from '../../services/importServices';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import '../../styles/AdminDashboard.css';

const LeadManagement = () => {
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [lastUpdatedTime, setLastUpdatedTime] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    source: ''
  });
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [agents, setAgents] = useState([]);
  const [selectedAgentForAssignment, setSelectedAgentForAssignment] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importData, setImportData] = useState([]);
  const [importAssignAgent, setImportAssignAgent] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [leadsPerPage, setLeadsPerPage] = useState(10);

  const { isAdmin, currentUser } = useAuth();
  const navigate = useNavigate();

  // Helper functions
  const hasActiveFilters = () => {
    return Object.values(filters).some(value => value !== '');
  };

  const getActiveFilterCount = () => {
    return Object.values(filters).filter(value => value !== '').length;
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      source: ''
    });
  };

  const clearAll = () => {
    clearSearch();
    clearFilters();
    setCurrentPage(1);
  };

  const displayLeads = (searchTerm || hasActiveFilters()) ? filteredLeads : leads;

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/leads');
      return;
    }
    loadLeads();
    loadAgents();
  }, [isAdmin, navigate]);

  useEffect(() => {
    filterLeads();
    setCurrentPage(1); // Reset to first page when filters change
  }, [leads, searchTerm, filters]);

  const loadLeads = async () => {
    try {
      setLoading(true);
      const leadsData = await adminService.getAllLeads();
      setLeads(leadsData);
      calculateLastUpdatedTime(leadsData);
    } catch (err) {
      setError('Error loading leads: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAgents = async () => {
    try {
      const agentsData = await adminService.getAllAgents();
      setAgents(agentsData);
    } catch (err) {
      console.error('Error loading agents:', err);
    }
  };

  const filterLeads = () => {
    let filtered = [...leads];

    // Apply text search
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(lead =>
        lead.fullName?.toLowerCase().includes(searchLower) ||
        lead.email?.toLowerCase().includes(searchLower) ||
        lead.mobileNo?.includes(searchTerm) ||
        lead.source?.toLowerCase().includes(searchLower) ||
        lead.assignedAgentEmail?.toLowerCase().includes(searchLower) ||
        lead.propertyType?.toLowerCase().includes(searchLower) ||
        lead.budget?.toString().includes(searchTerm) ||
        lead.id?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (filters.status) {
      filtered = filtered.filter(lead => lead.status === filters.status);
    }

    // Apply source filter
    if (filters.source) {
      filtered = filtered.filter(lead => lead.source === filters.source);
    }

    setFilteredLeads(filtered);
  };

  // Pagination logic
  const indexOfLastLead = currentPage * leadsPerPage;
  const indexOfFirstLead = indexOfLastLead - leadsPerPage;
  const currentLeads = (searchTerm || hasActiveFilters()) 
    ? filteredLeads.slice(indexOfFirstLead, indexOfLastLead)
    : leads.slice(indexOfFirstLead, indexOfLastLead);
  
  const totalLeads = (searchTerm || hasActiveFilters()) ? filteredLeads.length : leads.length;
  const totalPages = Math.ceil(totalLeads / leadsPerPage);

  // Generate page numbers for pagination
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  // Get visible page numbers (max 5 pages shown)
  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const calculateLastUpdatedTime = (leadsData) => {
    if (!leadsData || leadsData.length === 0) {
      setLastUpdatedTime(null);
      return;
    }

    let latestTime = null;

    leadsData.forEach(lead => {
      let leadTime = null;

      if (lead.lastModified && lead.lastModified.seconds) {
        leadTime = new Date(lead.lastModified.seconds * 1000);
      }
      else if (lead.updatedAt && lead.updatedAt.seconds) {
        leadTime = new Date(lead.updatedAt.seconds * 1000);
      }
      else if (lead.createdAt && lead.createdAt.seconds) {
        leadTime = new Date(lead.createdAt.seconds * 1000);
      }

      if (leadTime && (!latestTime || leadTime > latestTime)) {
        latestTime = leadTime;
      }
    });

    setLastUpdatedTime(latestTime);
  };

  const handleExport = async (format = 'excel') => {
    try {
      setExportLoading(true);

      const dataToExport = (searchTerm || hasActiveFilters()) ? filteredLeads : leads;

      if (dataToExport.length === 0) {
        setError('No leads to export');
        return;
      }

      if (format === 'excel') {
        await exportService.exportLeadsToExcel(dataToExport, 'leads_export');
      } else {
        await exportService.exportLeadsToCSV(dataToExport, 'leads_export');
      }

      setError('');
    } catch (err) {
      setError('Error exporting leads: ' + err.message);
    } finally {
      setExportLoading(false);
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await adminService.updateLeadStatus(leadId, newStatus);
      
      // Update local state
      setLeads(prevLeads => 
        prevLeads.map(lead => 
          lead.id === leadId 
            ? { ...lead, status: newStatus, updatedAt: new Date() }
            : lead
        )
      );
      
      setError('');
    } catch (err) {
      setError('Error updating status: ' + err.message);
    }
  };

  // FIXED: Delete lead function
  const confirmDelete = (lead) => {
    if (!lead || !lead.id) {
      setError('Cannot delete: Lead information is missing');
      return;
    }
    setSelectedLead(lead);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedLead || !selectedLead.id) {
      setError('Cannot delete: Lead ID is missing');
      setShowDeleteModal(false);
      return;
    }

    try {
      const result = await adminService.deleteLead(selectedLead.id);
      
      if (result.success) {
        // Remove lead from state
        setLeads(prevLeads => prevLeads.filter(lead => lead.id !== selectedLead.id));
        setFilteredLeads(prev => prev.filter(lead => lead.id !== selectedLead.id));
        
        setShowDeleteModal(false);
        setSelectedLead(null);
        setError('');
        
        // Show success message
        console.log('Lead deleted successfully:', selectedLead.id);
      }
    } catch (err) {
      setError('Error deleting lead: ' + err.message);
      console.error('Delete error:', err);
    }
  };

  // Import functionality
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImportFile(file);
    setError('');

    try {
      let parsedData;
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        parsedData = await importService.parseExcelFile(file);
      } else if (file.name.endsWith('.csv')) {
        parsedData = await importService.parseCSVFile(file);
      } else {
        throw new Error('Unsupported file format. Please use Excel or CSV.');
      }

      setImportData(parsedData);
    } catch (err) {
      setError('Error parsing file: ' + err.message);
      setImportFile(null);
      setImportData([]);
    }
  };

  const handleImport = async () => {
    if (!importFile || importData.length === 0) {
      setError('Please select a valid file first');
      return;
    }

    try {
      setImportLoading(true);
      setError('');

      const results = await importService.importLeads(importData, importAssignAgent || null);

      if (results.failed > 0) {
        setError(`Imported ${results.success} leads successfully. ${results.failed} failed. Check console for details.`);
        console.error('Import errors:', results.errors);
      } else {
        setError(`Successfully imported ${results.success} leads`);
      }

      // Refresh leads and close modal
      await loadLeads();
      setShowImportModal(false);
      setImportFile(null);
      setImportData([]);
      setImportAssignAgent('');
    } catch (err) {
      setError('Error importing leads: ' + err.message);
    } finally {
      setImportLoading(false);
    }
  };

  // Bulk assignment functionality
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allLeadIds = new Set(currentLeads.map(lead => lead.id));
      setSelectedLeads(allLeadIds);
    } else {
      setSelectedLeads(new Set());
    }
  };

  const handleSelectLead = (leadId, checked) => {
    const newSelected = new Set(selectedLeads);
    if (checked) {
      newSelected.add(leadId);
    } else {
      newSelected.delete(leadId);
    }
    setSelectedLeads(newSelected);
  };

  const handleBulkAssign = async () => {
    if (selectedLeads.size === 0) {
      setError('Please select at least one lead to assign');
      return;
    }

    if (!selectedAgentForAssignment) {
      setError('Please select an agent to assign leads to');
      return;
    }

    try {
      const results = await adminService.bulkAssignLeads(
        Array.from(selectedLeads), 
        selectedAgentForAssignment
      );

      setError(`Successfully assigned ${results.success} leads to agent`);
      if (results.failed > 0) {
        setError(prev => prev + `. ${results.failed} leads failed to assign`);
      }

      // Refresh leads and reset selection
      await loadLeads();
      setSelectedLeads(new Set());
      setShowAssignModal(false);
      setSelectedAgentForAssignment('');
    } catch (err) {
      setError('Error assigning leads: ' + err.message);
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'New': return 'primary';
      case 'Contacted': return 'info';
      case 'Visited': return 'secondary';
      case 'Qualified': return 'warning';
      case 'Closed': return 'success';
      case 'Lost': return 'danger';
      default: return 'secondary';
    }
  };

  const getSourceVariant = (source) => {
    switch (source) {
      case 'Website': return 'primary';
      case 'Referral': return 'success';
      case 'Social Media': return 'info';
      case 'Walk-in': return 'warning';
      case 'Call': return 'secondary';
      case 'Other': return 'dark';
      default: return 'dark';
    }
  };

  const formatLeadTime = (lead) => {
    if (lead.lastModified && lead.lastModified.seconds) {
      return new Date(lead.lastModified.seconds * 1000);
    }
    if (lead.updatedAt && lead.updatedAt.seconds) {
      return new Date(lead.updatedAt.seconds * 1000);
    }
    if (lead.createdAt && lead.createdAt.seconds) {
      return new Date(lead.createdAt.seconds * 1000);
    }
    return null;
  };

  if (!isAdmin()) {
    return (
      <Container>
        <Alert variant="danger">Access denied. Admin privileges required.</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p>Loading leads...</p>
      </Container>
    );
  }

  return (
    <Container>
      <Row className="mb-4">
        <Col>
          <h2>Lead Management</h2>
          <p className="text-muted">View and manage all leads in the system</p>
        </Col>
      </Row>

      {/* Combined Controls Card */}
      <Card className="mb-4 admin-card">
        <Card.Body>
          {/* Main Controls Row */}
          <Row>
            {/* Left Side - Stats (6 columns) */}
            <Col md={6}>
              <div className="d-flex flex-column">
                <div className="mb-1">
                  <strong className="fs-6">Total Leads: {leads.length}</strong>
                  {(searchTerm || hasActiveFilters()) && (
                    <span className="text-primary ms-2">
                      • Showing: {filteredLeads.length}
                    </span>
                  )}
                  {lastUpdatedTime && (
                  <div>
                    <small className="text-muted">
                      Last updated: {lastUpdatedTime.toLocaleTimeString()} {lastUpdatedTime.toLocaleDateString()}
                    </small>
                  </div>
                )}
                </div>
              </div>
            </Col>

            {/* Right Side - Search, Filters, Export (6 columns) */}
            <Col md={6}>
              <div className="d-flex gap-2 justify-content-end">
                    <InputGroup size="sm">
                    <InputGroup.Text>
                      <i className="bi bi-search"></i>
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search leads..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <Button variant="outline-secondary" onClick={clearSearch}>
                        <i className="bi bi-x"></i>
                      </Button>
                    )}
                  </InputGroup>
                    {/* Filter Dropdowns */}
                    {/* Status Filter */}
                    <Dropdown>
                      <Dropdown.Toggle
                        variant={filters.status ? "primary" : "outline-secondary"}
                        size="sm"
                        className="d-flex align-items-center"
                      >
                        <i className="bi bi-funnel me-1"></i>
                        Status
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item
                          onClick={() => setFilters(prev => ({ ...prev, status: '' }))}
                          className={!filters.status ? 'active' : ''}
                        >
                          All Statuses
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item
                          onClick={() => setFilters(prev => ({ ...prev, status: 'New' }))}
                          className={filters.status === 'New' ? 'active' : ''}
                        >
                          New
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => setFilters(prev => ({ ...prev, status: 'Contacted' }))}
                          className={filters.status === 'Contacted' ? 'active' : ''}
                        >
                          Contacted
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => setFilters(prev => ({ ...prev, status: 'Visited' }))}
                          className={filters.status === 'Visited' ? 'active' : ''}
                        >
                          Visited
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => setFilters(prev => ({ ...prev, status: 'Qualified' }))}
                          className={filters.status === 'Qualified' ? 'active' : ''}
                        >
                          Qualified
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => setFilters(prev => ({ ...prev, status: 'Closed' }))}
                          className={filters.status === 'Closed' ? 'active' : ''}
                        >
                          Closed
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => setFilters(prev => ({ ...prev, status: 'Lost' }))}
                          className={filters.status === 'Lost' ? 'active' : ''}
                        >
                          Lost
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>

                    {/* Source Filter */}
                    <Dropdown>
                      <Dropdown.Toggle
                        variant={filters.source ? "primary" : "outline-secondary"}
                        size="sm"
                        className="d-flex align-items-center"
                      >
                        <i className="bi bi-funnel me-1"></i>
                        Source
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item
                          onClick={() => setFilters(prev => ({ ...prev, source: '' }))}
                          className={!filters.source ? 'active' : ''}
                        >
                          All Sources
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item
                          onClick={() => setFilters(prev => ({ ...prev, source: 'Website' }))}
                          className={filters.source === 'Website' ? 'active' : ''}
                        >
                          Website
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => setFilters(prev => ({ ...prev, source: 'Referral' }))}
                          className={filters.source === 'Referral' ? 'active' : ''}
                        >
                          Referral
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => setFilters(prev => ({ ...prev, source: 'Social Media' }))}
                          className={filters.source === 'Social Media' ? 'active' : ''}
                        >
                          Social Media
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => setFilters(prev => ({ ...prev, source: 'Walk-in' }))}
                          className={filters.source === 'Walk-in' ? 'active' : ''}
                        >
                          Walk-in
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => setFilters(prev => ({ ...prev, source: 'Call' }))}
                          className={filters.source === 'Call' ? 'active' : ''}
                        >
                          Call
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => setFilters(prev => ({ ...prev, source: 'Other' }))}
                          className={filters.source === 'Other' ? 'active' : ''}
                        >
                          Other
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>

                    {/* Import Button */}
                    <Button
                      variant="info"
                      size="sm"
                      onClick={() => setShowImportModal(true)}
                      className="d-flex align-items-center"
                    >
                      <i className="bi bi-upload me-1"></i>
                      Import
                    </Button>

                    {/* Bulk Assign Button */}
                    {selectedLeads.size > 0 && (
                      <Button
                        variant="warning"
                        size="sm"
                        onClick={() => setShowAssignModal(true)}
                        className="d-flex align-items-center"
                      >
                        <i className="bi bi-person-plus me-1"></i>
                        Assign ({selectedLeads.size})
                      </Button>
                    )}

                    {/* Export Button */}
                    <Dropdown as={ButtonGroup}>
                      <Button
                        variant="success"
                        disabled={exportLoading || displayLeads.length === 0}
                        onClick={() => handleExport('excel')}
                        size="sm"
                        className='d-flex gap-1'
                      >Export
                        {exportLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1"></span>
                          </>
                        ) : (
                          <>
                            <i className="bi bi-file-earmark-excel"></i>
                          </>
                        )}
                      </Button>
                      <Dropdown.Toggle split variant="success" size="sm" />
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => handleExport('excel')}>
                          <i className="bi bi-file-earmark-excel me-2"></i>
                          Export to Excel
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleExport('csv')}>
                          <i className="bi bi-file-earmark-text me-2"></i>
                          Export to CSV
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
            </Col>
          </Row>

          {/* Active Filters Display */}
          {(searchTerm || hasActiveFilters()) && (
            <Row className="mt-3">
              <Col>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <small className="text-muted">Active filters:</small>

                  {searchTerm && (
                    <Badge bg="info" className="d-flex align-items-center px-2 py-1">
                      Search: "{searchTerm}"
                      <Button
                        variant="link"
                        className="text-white p-0 ms-2 border-0"
                        style={{ fontSize: '0.7em' }}
                        onClick={clearSearch}
                      >
                        <i className="bi bi-x"></i>
                      </Button>
                    </Badge>
                  )}

                  {filters.status && (
                    <Badge bg="secondary" className="d-flex align-items-center px-2 py-1">
                      Status: {filters.status}
                      <Button
                        variant="link"
                        className="text-white p-0 ms-2 border-0"
                        style={{ fontSize: '0.7em' }}
                        onClick={() => setFilters(prev => ({ ...prev, status: '' }))}
                      >
                        <i className="bi bi-x"></i>
                      </Button>
                    </Badge>
                  )}

                  {filters.source && (
                    <Badge bg="secondary" className="d-flex align-items-center px-2 py-1">
                      Source: {filters.source}
                      <Button
                        variant="link"
                        className="text-white p-0 ms-2 border-0"
                        style={{ fontSize: '0.7em' }}
                        onClick={() => setFilters(prev => ({ ...prev, source: '' }))}
                      >
                        <i className="bi bi-x"></i>
                      </Button>
                    </Badge>
                  )}
                </div>
              </Col>
            </Row>
          )}
          {(searchTerm || hasActiveFilters()) && (
                  <div className="mt-2">
                    <Button variant="outline-danger" size="sm" onClick={clearAll}>
                      Clear Filters
                    </Button>
                  </div>
                )}
        </Card.Body>
      </Card>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

      {/* Bulk Assignment Panel */}
      {selectedLeads.size > 0 && (
        <div className="bulk-assignment-panel mb-3">
          <Row className="align-items-center">
            <Col>
              <strong>
                <span className="selected-leads-count">{selectedLeads.size}</span> leads selected
              </strong>
            </Col>
            <Col xs="auto">
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => setSelectedLeads(new Set())}
              >
                Clear Selection
              </Button>
            </Col>
          </Row>
        </div>
      )}

      {/* Leads Table Card */}
      <Card className="admin-card">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            {(searchTerm || hasActiveFilters()) ? 'Filtered Leads' : 'All Leads'}
          </h5>
          <div className="d-flex align-items-center gap-3">
            <Badge bg={(searchTerm || hasActiveFilters()) ? 'info' : 'primary'} pill>
              {displayLeads.length} {(searchTerm || hasActiveFilters()) ? 'filtered' : 'total'} leads
            </Badge>
            
            {/* Items per page selector */}
            <div className="d-flex align-items-center">
              <small className="text-muted me-2">Show:</small>
              <Form.Select 
                size="sm" 
                style={{ width: '80px' }}
                value={leadsPerPage}
                onChange={(e) => {
                  setLeadsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Form.Select>
            </div>
          </div>
        </Card.Header>
          {displayLeads.length === 0 ? (
            <Alert variant="info" className="m-3">
              {(searchTerm || hasActiveFilters()) ? 'No leads found matching your search criteria.' : 'No leads found in the system.'}
              {(searchTerm || hasActiveFilters()) && (
                <Button variant="outline-primary" className="ms-2" onClick={clearAll}>
                  Clear Filters
                </Button>
              )}
              {!searchTerm && !hasActiveFilters() && (
                <Button variant="outline-primary" className="ms-2" onClick={() => setShowImportModal(true)}>
                  Import Leads
                </Button>
              )}
            </Alert>
          ) : (
            <>
              <Table responsive striped hover className="admin-table mb-0">
                <thead>
                  <tr>
                    <th width="50">
                      <Form.Check
                        type="checkbox"
                        onChange={handleSelectAll}
                        checked={selectedLeads.size === currentLeads.length && currentLeads.length > 0}
                      />
                    </th>
                    <th>ID</th>
                    <th>Lead Info</th>
                    <th>Contact</th>
                    <th>Source</th>
                    <th>Assigned Agent</th>
                    <th>Status</th>
                    <th>Last Action</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentLeads.map((lead, index) => {
                    const leadTime = formatLeadTime(lead);
                    const actualIndex = indexOfFirstLead + index;

                    return (
                      <tr key={lead.id}>
                        <td>
                          <Form.Check
                            type="checkbox"
                            className="lead-checkbox"
                            checked={selectedLeads.has(lead.id)}
                            onChange={(e) => handleSelectLead(lead.id, e.target.checked)}
                          />
                        </td>
                        <td>
                          <strong>{actualIndex + 1}</strong>
                        </td>
                        <td>
                          <strong>{lead.fullName}</strong>
                          <br />
                          <small className="text-muted">{lead.propertyType} • Rs.{lead.budget?.toLocaleString()}</small>
                        </td>
                        <td>
                          <div>{lead.email}</div>
                          <small className="text-muted">{lead.mobileNo}</small>
                        </td>
                        <td>
                          <Badge bg={getSourceVariant(lead.source)}>
                            {lead.source || 'Unknown'}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg="light" text="dark">
                            {lead.assignedAgentEmail || 'Unassigned'}
                          </Badge>
                        </td>
                        <td>
                          <Form.Select
                            size="sm"
                            value={lead.status}
                            onChange={(e) => handleStatusChange(lead.id, e.target.value)}
                          >
                            <option value="New">New</option>
                            <option value="Contacted">Contacted</option>
                            <option value="Visited">Visited</option>
                            <option value="Qualified">Qualified</option>
                            <option value="Closed">Closed</option>
                            <option value="Lost">Lost</option>
                          </Form.Select>
                        </td>
                        <td>
                          {leadTime ? (
                            <small className="text-muted">
                              {leadTime.toLocaleTimeString()}
                              <br />
                              {leadTime.toLocaleDateString()}
                            </small>
                          ) : (
                            <small className="text-muted">N/A</small>
                          )}
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              as={Link}
                              to={`/admin/leads/${lead.id}`}
                              variant="outline-primary"
                              size="sm"
                              title="View Details"
                            >
                              <i className="bi bi-eye"></i>
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => confirmDelete(lead)}
                              title="Delete Lead"
                            >
                              <i className="bi bi-trash"></i>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-4 p-3 border-top">
                  <div>
                    <small className="text-muted">
                      Showing {indexOfFirstLead + 1} to {Math.min(indexOfLastLead, totalLeads)} of {totalLeads} entries
                    </small>
                  </div>
                  
                  <Pagination className="mb-0">
                    <Pagination.First 
                      onClick={() => setCurrentPage(1)} 
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev 
                      onClick={() => setCurrentPage(currentPage - 1)} 
                      disabled={currentPage === 1}
                    />
                    
                    {getVisiblePages().map((page, index) => (
                      page === '...' ? (
                        <Pagination.Ellipsis key={index} disabled />
                      ) : (
                        <Pagination.Item
                          key={index}
                          active={page === currentPage}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </Pagination.Item>
                      )
                    ))}
                    
                    <Pagination.Next 
                      onClick={() => setCurrentPage(currentPage + 1)} 
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last 
                      onClick={() => setCurrentPage(totalPages)} 
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedLead ? (
            <>
              <p>Are you sure you want to delete lead for <strong>{selectedLead.fullName}</strong>?</p>
              <p className="text-danger">This action cannot be undone.</p>
              <div className="bg-light p-3 rounded">
                <strong>Lead Details:</strong><br />
                Name: {selectedLead.fullName}<br />
                Email: {selectedLead.email}<br />
                Phone: {selectedLead.mobileNo || 'N/A'}<br />
                Status: <Badge bg={getStatusVariant(selectedLead.status)}>{selectedLead.status}</Badge>
              </div>
            </>
          ) : (
            <Alert variant="danger">Lead information not available</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDelete}
            disabled={!selectedLead || !selectedLead.id}
          >
            Delete Lead
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Import Leads Modal */}
      <Modal show={showImportModal} onHide={() => setShowImportModal(false)} size="lg" className="import-modal">
        <Modal.Header closeButton>
          <Modal.Title>Import Leads</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <div 
                className="import-dropzone"
                onClick={() => document.getElementById('import-file').click()}
              >
                <i className="bi bi-cloud-upload fs-1 text-primary mb-3"></i>
                <h5>Upload File</h5>
                <p className="text-muted">Click to select Excel or CSV file</p>
                <small className="text-muted">Supports .xlsx, .xls, .csv</small>
              </div>
              <input
                type="file"
                id="import-file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              
              {importFile && (
                <Alert variant="success" className="mt-3">
                  <i className="bi bi-check-circle me-2"></i>
                  File selected: {importFile.name}
                  <br />
                  <small>Found {importData.length} leads</small>
                </Alert>
              )}
            </Col>
            
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Assign to Agent (Optional)</Form.Label>
                <Form.Select
                  value={importAssignAgent}
                  onChange={(e) => setImportAssignAgent(e.target.value)}
                >
                  <option value="">Keep Unassigned</option>
                  {agents.map(agent => (
                    <option key={agent.id} value={agent.email}>
                      {agent.firstName} {agent.lastName} ({agent.email})
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Assign all imported leads to a specific agent
                </Form.Text>
              </Form.Group>

              <div className="d-grid gap-2">
                <Button
                  variant="primary"
                  onClick={handleImport}
                  disabled={!importFile || importLoading}
                >
                  {importLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Importing...
                    </>
                  ) : (
                    `Import ${importData.length} Leads`
                  )}
                </Button>
                
                <Button
                  variant="outline-secondary"
                  onClick={() => importService.downloadTemplate('excel')}
                >
                  <i className="bi bi-download me-2"></i>
                  Download Template
                </Button>
              </div>
            </Col>
          </Row>

          {importData.length > 0 && (
            <div className="mt-3">
              <h6>Preview ({importData.length} leads)</h6>
              <div className="import-preview">
                <Table size="sm" striped>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Property Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importData.slice(0, 5).map((lead, index) => (
                      <tr key={index}>
                        <td>{lead.fullName}</td>
                        <td>{lead.email}</td>
                        <td>{lead.mobileNo}</td>
                        <td>{lead.propertyType}</td>
                      </tr>
                    ))}
                    {importData.length > 5 && (
                      <tr>
                        <td colSpan="4" className="text-center text-muted">
                          ... and {importData.length - 5} more leads
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Bulk Assign Modal */}
      <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Assign Selected Leads</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            You are about to assign <strong>{selectedLeads.size}</strong> leads to an agent.
          </p>
          
          <Form.Group className="mb-3">
            <Form.Label>Select Agent</Form.Label>
            <Form.Select
              value={selectedAgentForAssignment}
              onChange={(e) => setSelectedAgentForAssignment(e.target.value)}
              required
            >
              <option value="">Choose an agent...</option>
              {agents.map(agent => (
                <option key={agent.id} value={agent.email}>
                  {agent.firstName} {agent.lastName} ({agent.email})
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Alert variant="warning" className="mt-3">
            <i className="bi bi-exclamation-triangle me-2"></i>
            This action will reassign all selected leads to the chosen agent.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAssignModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleBulkAssign}
            disabled={!selectedAgentForAssignment}
          >
            Assign {selectedLeads.size} Leads
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default LeadManagement;